/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { ResourceManagerService } from '../resource-manager.service';
import { CommunicationService } from 'src/app/_services/communication/logic/commumication.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ServerConfig } from 'src/app/_model/server-config';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { IdentityServerStorageDaoService } from '../../dao/identity-server-storage-dao/identity-server-storage-dao.service';
import { IdentityServerDaoService } from '../../dao/identity-server-dao.service';
import { ErrorService } from 'src/app/_services/error/error.service';
import { AlertService } from 'src/app/_services/alert/alert.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads Resources like Translation files
 */
@Injectable()
export class ResourceManagerLogicService implements ResourceManagerService {
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private commService: CommunicationService,
    private http: HttpClient,
    private translate: TranslateService,
    private identityServerStorage: IdentityServerDaoService,
    private authService: AuthenticationService,
    private errorService: ErrorService,
    private alertService: AlertService,
  ) {
    this.loadLocalIdentityServer();
    this.commService.serverConfig$.subscribe({
      next: async (config) => {
        logger.log(
          this.constructor.name,
          '- Received new server config',
          config,
        );
        if (config) {
          try {
            await this.sync(APILevel.UNKNOWN, null);
          } catch (error) {
            this.logger.log(
              this.constructor.name,
              'error in subscribe sync',
              error,
            );
          }
        }
      },
    });
  }

  public async transmitAnonymousLogs(passphrase: string) {
    this.logger.log(this.constructor.name, '- Transmit Anonymous Logs to IMS');

    let serverConfig = this.commService.serverConfig$.getValue();

    const remote_path =
      serverConfig.host +
      ':' +
      serverConfig.port +
      serverConfig.queryString +
      'exportloganonym';

    this.logger.log(
      this.constructor.name,
      '- load anonymous logs to',
      remote_path,
    );

    try {
      let logs = await this.logger.exportLogs();

      let response = await this.http
        .post(
          remote_path,
          {
            passphrase: passphrase,
            logs: logs,
          },
          { observe: 'response' },
        )
        .toPromise();
      this.logger.log(
        this.constructor.name,
        '- Exported anonymous logs with response: (',
        response.status,
        ') ',
        response,
      );
      this.alertService.showAlert('Success', null, 'Erfolgreich exportiert.');
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error exporting anonymous logs: ',
        error?.message,
        error.status,
      );
      let message: string;
      if (error.status === 403) {
        message = await this.translate
          .get('SERVICE.RESOURCEMANAGER.EXPORTLOG.ERROR.FORBIDDEN')
          .toPromise();
      } else {
        message = await this.translate
          .get('SERVICE.RESOURCEMANAGER.EXPORTLOG.ERROR.UNKNOWN')
          .toPromise();
      }

      this.errorService.showWarningAlert(message, error);
    }
  }

  // Resetable
  public async reset() {
    await this.identityServerStorage.reset();
  }

  public async clear() {
    await this.identityServerStorage.reset();
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    this.logger.log(this.constructor.name, '- Start Syncing');
    this.clear();
    let serverConfig = this.commService.serverConfig$.getValue();
    if (serverConfig) {
      try {
        // JB20210520: Use this line of code to transmit the logs
        // anonymous at the app start. This can be used even if the
        // app is not starting and crashing at startup.
        // According anonymous log secret needs to be set on the
        // server
        // this.transmitAnonymousLogs("emergency");
        await this.commService.loadRemoteIdentityServer(serverConfig);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Could not load remote ressources',
          error?.message,
        );
      }
    }
  }

  public async postsync(): Promise<void> {}
  public async presync(): Promise<void> {}

  // Private functions
  private async loadLocalIdentityServer() {
    try {
      this.logger.log(this.constructor.name, '- Load local Identity Server');

      let identityServer =
        await this.identityServerStorage.getIdentitiyServer();

      if (identityServer) {
        this.authService.setIdentityServer(identityServer['issuer']);
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Identity Server: ',
        error?.message,
      );
      throw error;
    }
  }
}
